import React, { useEffect, useState } from 'react';
import { ProjectAnalysis } from './project-analysis.type';
import { Division, PredictedLineItemWithDivision } from '../glossary/glossary.type';
import Card from '../general/Card';
import { useNavigate } from 'react-router-dom';
import { Grid, Alert, Stack } from '@mui/material';
import { Button, Typography } from 'components/index';
import { flattenArray, formatCurrency, joinDateAndUser } from '../../common/utils/common.util';
import { AttachMoney, Save, Assessment } from '@mui/icons-material';
import { ProjectCostPerUnit, ReportGeneralInfo } from '../report/report.type';
import Footer from '../general/Footer';
import { usePrompt } from '../AppPrompt';
import { useQueryClient } from 'react-query';
import keys from '../../common/react-query/data-keys';
import { DataGrid } from '@mui/x-data-grid';
import { useAppContext } from 'common/AppContext';
import { REPORT_COLORS, HEIGHTS } from '../report/constants';
import { getTotalsRow } from '../report/report.util';
import ProjectAnalysisDivisionTable from './ProjectAnalysisDivisionTable';
import { getReportColumns } from '../report/ReportColumns';
import ProjectAnalysisLineItemEditDialog from './ProjectAnalysisLineItemEditDialog';
import ProjectDrawSelectionDialog from '../report/ProjectDrawSelectionDialog';
import useProjectDivisionsDraws from './useProjectAnalysisDivisionDraws';
import { FileUploadErrorDTO, ProjectDraw, ProjectLineItemDTO } from '../project/project.type';
import AutoAssignDivisionsDialog from '../../components/report/AutoAssignDivisionsDialog';
import useAutoDivisionMapper from '../../components/glossary/useAutoDivisionMapper';
import { updateProjectDivisionDraws } from '../project/project.api';
import { toast } from 'react-toastify';
import { handleError } from 'common/utils/util.errors';
import { generateReport } from '../report/report.api';
import { ROUTES } from 'common/constants/urls';
import { UNDEFINED_CHANGE_ORDER_DIVISION_CODE } from 'common/constants/general';
import GeneralInfoCard from '../report/GeneralInfoCard';

interface Props {
  projectAnalysis: ProjectAnalysis;
  divisions: Division[];
  projectAnalysisDraws: ProjectDraw[];
  isProjectAnalysisDirty: boolean;
  isProjectAnalysisLoading: boolean;
  updateProjectAnalysis: (updatedProjectAnalysis: ProjectAnalysis) => void;
  resetProjectAnalysis: () => void;
  saveProjectAnalysis: () => void;
  refetchProjectAnalysis: () => void;
  projectErrors: FileUploadErrorDTO[];
}

const ProjectAnalysisRenderer: React.FC<Props> = ({
  projectAnalysis,
  divisions,
  projectAnalysisDraws,
  isProjectAnalysisDirty,
  isProjectAnalysisLoading,
  updateProjectAnalysis,
  resetProjectAnalysis,
  saveProjectAnalysis,
  refetchProjectAnalysis,
  projectErrors,
}) => {
  const navigate = useNavigate();
  const { getConfirmation } = usePrompt();
  const queryClient = useQueryClient();
  const { setPageHeaderText } = useAppContext();
  const [generateReportInProgress, setGenerateReportInProgress] = useState(false);
  const [divisionTables, setDivisionTables] = useState<{ [key: string]: any[] }>({});
  const [selectedLineItem, setSelectedLineItem] = useState<ProjectLineItemDTO | null>(null);
  const [selectedDivision, setSelectedDivision] = useState<Division | null>(null);
  const [lineItemEditDialogOpen, setLineItemEditDialogOpen] = useState(false);
  const { selectedProjectDraws, handleProjectDrawSelectionChange } = useProjectDivisionsDraws(projectAnalysis.projectId);
  const { openAutoAssignDialog, isAutoAssignDialogOpen, lineItemsToAutoAssign, predictingDivisions, setIsAutoAssignDialogOpen } =
    useAutoDivisionMapper(divisions);

  const updateDivisions = (updatedLineItems: PredictedLineItemWithDivision[]) => {
    const updatedPALineItems = projectAnalysis.lineItems.map(li => {
      const updatedLineItem = updatedLineItems.find(uli => uli.id === li.id);
      if (updatedLineItem) {
        return { ...li, lineItem: { ...li.lineItem, division: updatedLineItem.division ?? undefined }, isEdited: true };
      }
      return li;
    });
    updateProjectAnalysis({ ...projectAnalysis, lineItems: updatedPALineItems });
  };

  useEffect(() => {
    if (projectAnalysis.projectTitle) {
      setPageHeaderText(projectAnalysis.projectNumber + ' ' + projectAnalysis.projectTitle);
    }
    return () => {
      setPageHeaderText('');
    };
  }, [projectAnalysis.projectTitle]);

  const totalBudget = React.useMemo(() => {
    return projectAnalysis.lineItems
      .filter(li => !li.isDeleted)
      .reduce((acc, row) => {
        const rowTotal = row.originalBudget + row.reallocation + row.changeOrder;
        return acc + rowTotal;
      }, 0);
  }, [projectAnalysis.lineItems]);

  const costPerUnit: ProjectCostPerUnit = React.useMemo(() => {
    const grossBuildingSF = projectAnalysis?.grossBuildingSF ?? 0;
    const netRentableSF = projectAnalysis?.netRentableSF ?? 0;
    const buildingCount = projectAnalysis?.buildingCount ?? 0;
    const storyCount = projectAnalysis?.storyCount ?? 0;
    const unitCount = projectAnalysis?.unitCount ?? 0;
    const siteAcreage = projectAnalysis?.siteAcreage ?? 0;

    return {
      buildingGSF: grossBuildingSF > 0 ? totalBudget / grossBuildingSF : 0,
      buildingNRSF: netRentableSF > 0 ? totalBudget / netRentableSF : 0,
      building: buildingCount > 0 ? totalBudget / buildingCount : 0,
      story: storyCount > 0 ? totalBudget / storyCount : 0,
      unit: unitCount > 0 ? totalBudget / unitCount : 0,
      acre: siteAcreage > 0 ? totalBudget / siteAcreage : 0,
    };
  }, [projectAnalysis, totalBudget]);

  // Arrange line items into division tables
  useEffect(() => {
    categorizeLineItemsIntoDivisions(projectAnalysis, divisions, setDivisionTables);
  }, [divisions, projectAnalysis]);

  const sortedDivisions = React.useMemo(() => {
    return [...divisions].sort((a, b) => Number(a.code) - Number(b.code));
  }, [divisions]);

  const sortedTables = React.useMemo(() => {
    const sortedTables: any = [];
    sortedDivisions.forEach(division => {
      sortedTables.push(divisionTables[division.id] && divisionTables[division.id].length > 0 ? divisionTables[division.id] : []);
    });
    return sortedTables;
  }, [divisionTables, sortedDivisions]);

  const hardCostSubTotalRow = React.useMemo(() => {
    return [
      getTotalsRow(
        'HARD COST SUBTOTAL',
        flattenArray(sortedTables, 0, 15),
        projectAnalysis?.grossBuildingSF ?? 0,
        'no-division',
        undefined,
        undefined,
        undefined,
        true,
      ),
    ];
  }, [sortedTables, projectAnalysis]);

  const projectedHardCostSubTotalRow = React.useMemo(() => {
    const contractor_overhead_and_contingency = [...sortedTables[16], ...sortedTables[17]];
    const projectedTotalHardCost = [...contractor_overhead_and_contingency, ...hardCostSubTotalRow];
    return [
      getTotalsRow(
        'TOTAL PROJECTED HARD COSTS',
        projectedTotalHardCost,
        projectAnalysis?.grossBuildingSF ?? 0,
        'no-division',
        undefined,
        undefined,
        undefined,
        true,
      ),
    ];
  }, [sortedTables, projectAnalysis]);

  const softCostSubTotalRow = React.useMemo(() => {
    return [
      getTotalsRow(
        'TOTAL SOFT COSTS',
        [...sortedTables[18], ...sortedTables[19]],
        projectAnalysis?.grossBuildingSF ?? 0,
        'no-division',
        undefined,
        undefined,
        undefined,
        true,
      ),
    ];
  }, [sortedTables, projectAnalysis]);

  const totalProjectCostsRow = React.useMemo(() => {
    return [
      getTotalsRow(
        'TOTAL PROJECTED COSTS',
        [...projectedHardCostSubTotalRow, ...softCostSubTotalRow],
        projectAnalysis?.grossBuildingSF ?? 0,
        'no-division',
        undefined,
        undefined,
        undefined,
        true,
      ),
    ];
  }, [sortedTables, projectAnalysis]);

  const generalInfo = React.useMemo(() => {
    return {
      reportName: projectAnalysis.projectTitle,
      grossBuildingSF: projectAnalysis.grossBuildingSF,
      netRentableSF: projectAnalysis.netRentableSF,
      buildingCount: projectAnalysis.buildingCount,
      storyCount: projectAnalysis.storyCount,
      unitCount: projectAnalysis.unitCount,
      siteAcreage: projectAnalysis.siteAcreage,
    };
  }, [projectAnalysis]);

  const updateLineItem = (updatedLineItem: ProjectLineItemDTO) => {
    const updatedLineItems = projectAnalysis.lineItems.map(lineItem => {
      if (lineItem.id === updatedLineItem.id) {
        return { ...updatedLineItem, isEdited: true };
      }
      return lineItem;
    });
    updateProjectAnalysis({ ...projectAnalysis, lineItems: updatedLineItems });
  };

  const divisionTablesRendered = React.useMemo(() => {
    const tablesRendered = sortedDivisions.map((division, index) => {
      const gridData = sortedTables[index];

      if (division.code === UNDEFINED_CHANGE_ORDER_DIVISION_CODE) return null;

      return (
        <React.Fragment key={division.id}>
          <ProjectAnalysisDivisionTable
            key={division.id}
            division={division}
            gridData={gridData}
            divisions={divisions}
            projectAnalysis={projectAnalysis}
            updateProjectAnalysis={updateProjectAnalysis}
            updateLineItem={updateLineItem}
            setSelectedLineItem={setSelectedLineItem}
            setLineItemEditDialogOpen={setLineItemEditDialogOpen}
            handleOpenProjectDialog={setSelectedDivision}
            refetchProjectAnalysis={refetchProjectAnalysis}
            hardCostSubTotal={projectedHardCostSubTotalRow[0].totalBudget}
            reportGeneralInfo={generalInfo}
            projectDraws={projectAnalysisDraws}
          />
          {index == 15 ? (
            <Grid size={{ xs: 12 }} key={'hard-cost-totals'} minHeight={HEIGHTS.SUBTOTAL_HEIGHT}>
              <DataGrid
                rows={hardCostSubTotalRow}
                columns={getReportColumns(null, null, true)}
                checkboxSelection={true}
                isCellEditable={() => false}
                hideFooter
                sx={{
                  ...reportTotalsCommonStyles,
                  '& .projectHeader': {
                    backgroundColor: REPORT_COLORS.HARD_COST_SUBTOTAL_COLOR,
                  },
                  '& .projectTotal': {
                    backgroundColor: REPORT_COLORS.HARD_COST_SUBTOTAL_COLOR,
                  },
                }}
              />
            </Grid>
          ) : null}
          {index == 17 ? (
            <Grid size={{ xs: 12 }} key={'projected-hard-cost-totals'} minHeight={HEIGHTS.SUBTOTAL_HEIGHT}>
              <DataGrid
                rows={projectedHardCostSubTotalRow}
                columns={getReportColumns(null, null, true)}
                checkboxSelection={true}
                isCellEditable={() => false}
                hideFooter
                sx={{
                  ...reportTotalsCommonStyles,
                  '& .projectHeader': {
                    backgroundColor: REPORT_COLORS.PROJECTED_HARD_COST_SUBTOTAL_COLOR,
                  },
                  '& .projectTotal': {
                    backgroundColor: REPORT_COLORS.PROJECTED_HARD_COST_SUBTOTAL_COLOR,
                  },
                }}
              />
            </Grid>
          ) : null}
          {index == 19 ? (
            <Grid size={{ xs: 12 }} key={'soft-cost-totals'} minHeight={HEIGHTS.SUBTOTAL_HEIGHT}>
              <DataGrid
                rows={softCostSubTotalRow}
                columns={getReportColumns(null, null, true)}
                checkboxSelection={true}
                isCellEditable={() => false}
                hideFooter
                sx={{
                  ...reportTotalsCommonStyles,
                  '& .projectHeader': {
                    backgroundColor: REPORT_COLORS.SOFT_COST_SUBTOTAL_COLOR,
                  },
                  '& .projectTotal': {
                    backgroundColor: REPORT_COLORS.SOFT_COST_SUBTOTAL_COLOR,
                  },
                }}
              />
            </Grid>
          ) : null}
        </React.Fragment>
      );
    });

    tablesRendered.push(
      <Grid size={{ xs: 12 }} key={'total-project-costs'} minHeight={HEIGHTS.SUBTOTAL_HEIGHT}>
        <DataGrid
          rows={totalProjectCostsRow}
          columns={getReportColumns(null, null, true)}
          checkboxSelection={true}
          isCellEditable={() => false}
          hideFooter
          sx={{
            ...reportTotalsCommonStyles,
            '& .projectHeader': {
              backgroundColor: REPORT_COLORS.TOTAL_COST_COLOR,
            },
            '& .projectTotal': {
              backgroundColor: REPORT_COLORS.TOTAL_COST_COLOR,
            },
          }}
        />
      </Grid>,
    );

    // Handling "No Division" data grid
    tablesRendered.unshift(
      <ProjectAnalysisDivisionTable
        key={'no-division'}
        division={null}
        gridData={divisionTables['no-division'] ?? []}
        divisions={divisions}
        projectAnalysis={projectAnalysis}
        updateLineItem={updateLineItem}
        refetchProjectAnalysis={refetchProjectAnalysis}
        updateProjectAnalysis={updateProjectAnalysis}
        setSelectedLineItem={setSelectedLineItem}
        setLineItemEditDialogOpen={setLineItemEditDialogOpen}
        handleOpenProjectDialog={setSelectedDivision}
        reportGeneralInfo={generalInfo}
        openAutoAssignDialog={openAutoAssignDialog}
        predictingDivisions={predictingDivisions}
        projectDraws={projectAnalysisDraws}
      />,
    );

    return tablesRendered;
  }, [divisionTables, sortedDivisions, sortedTables, projectAnalysis, openAutoAssignDialog]);

  const updateProjectAnalysisMetadata = (generalInfo: ReportGeneralInfo) => {
    updateProjectAnalysis({
      ...projectAnalysis,
      projectTitle: generalInfo.reportName ?? '',
      grossBuildingSF: generalInfo.grossBuildingSF,
      netRentableSF: generalInfo.netRentableSF,
      buildingCount: generalInfo.buildingCount,
      storyCount: generalInfo.storyCount,
      unitCount: generalInfo.unitCount,
      siteAcreage: generalInfo.siteAcreage,
      isEditedLocally: true,
    });
  };

  const updateDivisionDraws = async (divisionId: string, newDrawsIds: string[]) => {
    await updateProjectDivisionDraws(projectAnalysis.projectId, {
      divisionId: divisionId,
      newDrawIds: newDrawsIds,
    });
    queryClient.invalidateQueries([keys.projectAnalysis]);
  };

  const handleGenerateReport = async () => {
    if (!projectAnalysis.projectId) {
      toast.error('Invalid project');
      return;
    }
    setGenerateReportInProgress(true);
    try {
      const reportName = `Cost Analysis Report: ${projectAnalysis.projectTitle} (${projectAnalysis.projectNumber})`;
      const reportGeneralInfo = {
        reportName: reportName,
      };
      const report = await generateReport(projectAnalysis.projectId, reportGeneralInfo);
      navigate(`${ROUTES.CA_REPORTS}/${report.id}`, { state: { reportId: report.id } });
      toast.success('Report created successfully');
    } catch (error) {
      handleError(error);
    } finally {
      setGenerateReportInProgress(false);
    }
  };

  return (
    <>
      {projectAnalysis.errorMessage ? <Alert severity='error'>{projectAnalysis.errorMessage}</Alert> : null}
      <Grid container spacing={2} pb={2}>
        <Grid size={{ xs: 6 }}>
          <GeneralInfoCard
            generalInfo={generalInfo}
            updateGeneralInfo={updateProjectAnalysisMetadata}
            cardProps={{ itemsPerRow: 2 }}
            projectErrors={projectErrors}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Card
            icon={<AttachMoney color='primary' />}
            heading='Cost Per Unit'
            backgroundColor={REPORT_COLORS.BLUE}
            data={{
              'Building GSF': costPerUnit.buildingGSF != 0 ? formatCurrency(costPerUnit.buildingGSF.toString()) : '--',
              'Building NRSF': costPerUnit.buildingNRSF != 0 ? formatCurrency(costPerUnit.buildingNRSF.toString()) : '--',
              Building: costPerUnit.building != 0 ? formatCurrency(costPerUnit.building.toString()) : '--',
              Story: costPerUnit.story != 0 ? formatCurrency(costPerUnit.story.toString()) : '--',
              Acre: costPerUnit.acre != 0 ? formatCurrency(costPerUnit.acre.toString()) : '--',
              Unit: costPerUnit.unit != 0 ? formatCurrency(costPerUnit.unit.toString()) : '--',
            }}
            itemsPerRow={2}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} overflow={'auto'}>
        {divisionTablesRendered}
      </Grid>
      <Grid size={{ xs: 12 }} key='grandTotals' mt={2}>
        <Card
          icon={<AttachMoney color='primary' />}
          heading='Grand Totals'
          data={{
            Budget: formatCurrency(totalBudget.toString()),
          }}
        />
      </Grid>
      <Footer height={HEIGHTS.FOOTER_HEIGHT}>
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} flex={1}>
          <Stack direction={'row'} spacing={1}>
            <Typography variant='subtitle2'>
              {joinDateAndUser(projectAnalysis.createdOn, projectAnalysis.createdBy, 'Uploaded On ')}
            </Typography>
          </Stack>
          <Stack direction={'row'} spacing={1}>
            {isProjectAnalysisDirty ? <Alert severity='warning'>Report has unsaved changes</Alert> : null}
          </Stack>
          <Stack direction={'row'} spacing={1}>
            {isProjectAnalysisDirty ? (
              <Button
                variant='contained'
                onClick={saveProjectAnalysis}
                disabled={!isProjectAnalysisDirty}
                startIcon={<Save />}
                loading={isProjectAnalysisLoading}
              >
                Submit Changes
              </Button>
            ) : (
              <Button
                variant='contained'
                onClick={handleGenerateReport}
                disabled={isProjectAnalysisDirty}
                startIcon={<Assessment />}
                loading={generateReportInProgress}
              >
                Generate Cost Analysis
              </Button>
            )}
          </Stack>
        </Stack>
      </Footer>
      <ProjectAnalysisLineItemEditDialog
        open={lineItemEditDialogOpen}
        onClose={() => setLineItemEditDialogOpen(false)}
        lineItem={selectedLineItem}
        updateLineItem={updateLineItem}
        divisions={divisions}
      />
      <AutoAssignDivisionsDialog
        open={isAutoAssignDialogOpen}
        onClose={() => setIsAutoAssignDialogOpen(false)}
        lineItems={lineItemsToAutoAssign}
        updateDivisions={updateDivisions}
        divisions={divisions}
        showToggle={false}
      />
      {selectedDivision ? (
        <ProjectDrawSelectionDialog
          open={selectedDivision != null}
          onClose={() => setSelectedDivision(null)}
          divisionId={selectedDivision.id}
          divisionCode={selectedDivision.code}
          divisionName={selectedDivision.name}
          allProjectDraws={projectAnalysisDraws}
          selectedProjectDraws={selectedProjectDraws}
          onSelectionChange={handleProjectDrawSelectionChange}
          updateDivisionDraws={updateDivisionDraws}
          isReportDirty={isProjectAnalysisDirty}
        />
      ) : null}
    </>
  );
};

const categorizeLineItemsIntoDivisions = (projectAnalysis: ProjectAnalysis, divisions: Division[], setDivisionTables: any) => {
  const lineItems = projectAnalysis.lineItems?.filter(li => !li.isDeleted) || [];

  const newDivisionData: { [key: string]: any[] } = {};

  divisions.forEach(division => {
    const divisionItems = lineItems.filter(item => {
      if (item.lineItem.division) return item.lineItem.division.id === division.id;
    });

    if (divisionItems.length > 0) {
      newDivisionData[division.id] = divisionItems.map(item => ({
        ...item,
        divisionName: division.name,
        isDivisionHeader: false,
      }));
    } else {
      newDivisionData[division.id] = [];
    }
  });

  const noDivisionItems = lineItems.filter(
    item => !item.lineItem.division || item.lineItem.division.code === UNDEFINED_CHANGE_ORDER_DIVISION_CODE,
  );
  if (noDivisionItems.length > 0) {
    newDivisionData['no-division'] = noDivisionItems.map(item => ({
      ...item,
      divisionName: 'No Division',
      isDivisionHeader: false,
    }));
  } else {
    newDivisionData['no-division'] = [];
  }

  setDivisionTables(newDivisionData);
};

const reportTotalsCommonStyles = {
  margin: '0px',
  fontSize: '0.7em',
  '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root, & .MuiDataGrid-columnHeaders .MuiCheckbox-root': {
    display: 'none',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: REPORT_COLORS.WHITE,
    borderBottom: 'none',
    maxHeight: 'unset !important',
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: REPORT_COLORS.WHITE,
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'normal',
    lineHeight: 1.2,
    height: 'auto',
    textOverflow: 'clip',
    maxHeight: 'unset !important',
  },
  '& .MuiDataGrid-cellContent': {
    whiteSpace: 'normal',
    lineHeight: 1.2,
    height: 'auto',
    textOverflow: 'clip',
    maxHeight: 'unset !important',
  },
  '& .MuiDataGrid-row, & .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
    backgroundColor: REPORT_COLORS.WHITE,
    fontWeight: 600,
  },
  '& .bolderTotalsDescription': {
    fontWeight: 1000,
  },
  '& .compHeader': {
    backgroundColor: REPORT_COLORS.BLUE,
    borderTop: '0.1px solid',
    borderColor: REPORT_COLORS.BORDER,
  },
  '& .compTotal': {
    backgroundColor: REPORT_COLORS.BLUE,
  },
  '& .separatorCol': {
    borderInline: '0.1px solid',
    borderColor: REPORT_COLORS.BORDER,
    borderBlock: 'none',
  },
  '& .leftBorder': {
    borderLeft: '0.1px solid',
    borderColor: REPORT_COLORS.BORDER,
  },
  '& .rightBorder': {
    borderRight: '0.1px solid',
    borderColor: REPORT_COLORS.BORDER,
  },
  '.MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer': {
    flexDirection: 'row',
  },
};

export default ProjectAnalysisRenderer;
